import Utils from '../Utils.js';

/**
 * **De qué sesión es un dato.**
 *
 * Las pantallas que sobreviven a un cierre de sesión —la vista izquierda con el menú, y cualquier
 * ruta con `keepAlive`— guardan en memoria datos de quien estuviera dentro. Cerrar sesión y entrar
 * con otra cuenta en la misma pestaña no las recrea: la instancia es la misma, con su saldo, sus
 * solicitudes, sus contadores y sus peticiones en vuelo. Si las dos cuentas miran el mismo año y la
 * misma pestaña, las claves de vigencia (`respuestaVigente`, `carrilPeticiones`) dicen que lo de
 * antes sigue valiendo, porque el año y la pestaña **no han cambiado**: lo que ha cambiado es de
 * quién son (revisión CODEX del PR #1735).
 *
 * Así que al año y a la pestaña les falta un tercer dato, y es este: **la sesión**. Una respuesta
 * —o un error— pedido con otra sesión no es de lo que se mira ahora, por mucho que coincida el
 * resto de la clave.
 *
 * ## Por qué el token y no el usuario
 *
 * `Utils.getUser()` no sirve de identidad: el usuario lo trae `Usuario.me()` **después** de abrir
 * la sesión (ver `usuarioActualizado.js`), así que pasaría de `null` al nombre dentro de una misma
 * sesión y tiraría respuestas buenas. El token se escribe en `saveSession` y se borra en
 * `removeSession`, los dos **antes** de avisar por el `EventBus`, y es además lo que `sendRequest`
 * pone en cada petición: «de qué sesión es esta respuesta» es exactamente «con qué token se pidió».
 *
 * ## Se lee del estado, no de los avisos
 *
 * A propósito: quien escuche `app:session:close` / `app:session:open` vacía lo que tiene, y esto
 * además permite comprobarlo **sin depender de haber recibido el aviso** —una pantalla con
 * `keepAlive` que no estaba montada, un `$off` de más, un cierre por 401 desde otro sitio—. Con el
 * estado delante la comprobación no puede quedarse desfasada.
 */

/**
 * Lo que se contesta sin sesión abierta. Es un valor de verdad y no `null` a propósito: en
 * `mismaClave` un `null` en la clave de la pantalla significa «todavía no se sabe de qué va esto» y
 * la respuesta mandaría, que es justo lo contrario de lo que hace falta al cerrar sesión.
 */
const SIN_SESION = 'sin-sesion';

export default function identidadSesion() {
    return Utils.getToken() || SIN_SESION;
}

/**
 * ¿Sigue abierta la sesión en la que empezó esto?
 *
 * Para las acciones —aprobar, denegar, anular, enviar—, que no van por el carril: lo que contesten
 * no se pinta, no se avisa y no recarga nada si entre medias se ha cambiado de cuenta.
 */
export function sigueLaSesion(identidad) {
    return identidad === identidadSesion();
}

/**
 * **¿Hay sesión para pedir algo?**
 *
 * Lo que mira quien vive fuera de una ruta —el menú de la vista izquierda— **antes de pedir**, no
 * al pintar. Sin token, una petición a un endpoint protegido solo puede traer un 401, así que no
 * se gasta (revisión CODEX del PR #1735).
 *
 * Lo que esto **no** es es la defensa contra el 401 que echa a otra sesión: esa no puede vivir en
 * una pantalla, porque la petición también puede salir autenticada y contestar después de un cambio
 * de cuenta. Vive en `cierreDeSesionPor401`, que es la pieza que comparten los tres manejadores de
 * 401 del repo, y las claves de vigencia de aquí siguen siendo lo otro: qué se pinta.
 *
 * Las pantallas de ruta no lo necesitan —`RoutesUtils.checkAuthPage` no las deja entrar sin
 * sesión—, y quien sí tiene que esperar lo hace por `app:auth-ready`, que es el aviso que da la
 * aplicación cuando ya hay cabecera que poner (`startSession` tras una recarga y `saveSession` tras
 * el login). Es el mismo baile que hace la campana de alertas, por lo mismo.
 */
export function haySesion() {
    return identidadSesion() !== SIN_SESION;
}
